import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import ChartFrame from "../charts/ChartFrame";
import DataTable from "../charts/DataTable";
import RankedBars from "../charts/RankedBars";
import { formatCount, formatDecimal, formatShare } from "../charts/format";
import { ALL_SHOWN } from "../../utils/analyticsCapabilities";
import ReplyLatency from "./ReplyLatency";
import Section from "./Section";
import StatTile from "./StatTile";

// A camada tecnica (spec do RPAA-286, decisoes 9 e 12; D7 do plano). So aparece quando a resposta a
// traz, e o backend so a manda a quem tem acesso total. As versoes e as origens vao em tabela e nao
// em grafico: sao conjuntos abertos, que a paleta fixa nao cobre.
const RankedTable = ({ title, note, breakdown }) => {
  const { translate, language } = useLanguage();
  const rows = Object.entries(breakdown.values).map(([key, value]) => ({ key, label: key, value: formatShare(value, breakdown.total, language) }));
  if (breakdown.unattributed > 0) {
    rows.push({ key: "unattributed", label: translate("analyticsUnattributed"), value: formatShare(breakdown.unattributed, breakdown.total, language) });
  }
  return (
    <ChartFrame title={title} note={note} empty={breakdown.total === 0}>
      <DataTable
        caption={title}
        columns={[
          { key: "label", label: translate("analyticsCategory") },
          { key: "value", label: translate("analyticsCount"), numeric: true },
        ]}
        rows={rows}
      />
    </ChartFrame>
  );
};

const TechnicalSection = ({ technical, previousComplete, cut, timeZone, capabilities = ALL_SHOWN }) => {
  const { translate, language } = useLanguage();
  const count = (value) => formatCount(value, language);
  const { auto_close: autoClose, jobs_late: late, voice_failures: voiceFailures } = technical;
  // Um backend antigo nao manda as falhas da voz: sem elas, ou com a voz desligada, nada se desenha.
  const showVoiceFailures = Boolean(voiceFailures) && capabilities.voice;
  const voiceFailuresDetail =
    voiceFailures && voiceFailures.failed_pct !== null
      ? translate("analyticsVoiceFailuresDetail", { pct: formatDecimal(voiceFailures.failed_pct, language) })
      : null;
  const phase = (enabled, hours) => (enabled ? translate("analyticsAutoCloseOn", { hours }) : translate("analyticsAutoCloseOff"));
  const warnings = [
    ...(late.counters ? [translate("analyticsCountersLate")] : []),
    ...(late.conversations ? [translate("analyticsConversationsLate")] : []),
    ...autoClose.warnings.map((warning) => translate(`analytics.warning.${warning}`)),
  ];
  return (
    <Section title={translate("analyticsTechnical")} note={translate("analyticsTechnicalNotes")}>
      <div
        role="status"
        className="lg:col-span-2 rounded-xl border p-4 text-sm space-y-1"
        style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-text-primary)" }}
      >
        {warnings.length === 0 ? <p>{translate("analyticsJobsOnTime")}</p> : warnings.map((warning) => <p key={warning}>{warning}</p>)}
        <p style={{ color: "var(--color-text-secondary)" }}>
          {translate("analyticsAutoCloseSettings", {
            bot: phase(autoClose.bot_enabled, autoClose.bot_window_hours),
            human: phase(autoClose.human_enabled, autoClose.human_window_hours),
            frozen: autoClose.frozen_after_hours,
          })}
        </p>
      </div>
      <div className="lg:col-span-2 grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatTile label={translate("analyticsBots")} value={count(technical.page_views.bots)} />
        <StatTile label={translate("analyticsBotOpens")} value={count(technical.opens.bots)} />
        <StatTile label={translate("analyticsBotConversations")} value={count(technical.conversations.bots)} />
        <StatTile label={translate("analyticsRestoreOpens")} value={count(technical.opens.restore)} />
        <StatTile label={translate("analyticsDockedOpens")} value={count(technical.opens.docked)} />
        <StatTile label={translate("analyticsSearchResults")} value={count(technical.product_search_results)} />
        <StatTile label={translate("analyticsFallbackReplies")} value={count(technical.fallback_replies)} info={translate("analyticsFallbackRepliesNote")} />
        {showVoiceFailures && <StatTile label={translate("analyticsVoiceFailures")} value={count(voiceFailures.failed)} detail={voiceFailuresDetail} info={translate("analyticsVoiceFailuresNote")} />}
      </div>
      {showVoiceFailures && voiceFailures.failed > 0 && (
        <RankedBars
          title={translate("analyticsVoiceFailureReasons")}
          breakdown={{ total: voiceFailures.to_assistant, values: voiceFailures.reasons, unattributed: 0 }}
          dimension="voice_outcome"
        />
      )}
      <ReplyLatency latency={technical.reply_latency} previousComplete={previousComplete} cut={cut} timeZone={timeZone} />
      <RankedBars title={translate("analyticsPageViewsBrowser")} breakdown={technical.page_views.browser} dimension="browser" />
      <RankedTable title={translate("analyticsPageViewsOrigin")} note={translate("analyticsOriginNote")} breakdown={technical.page_views.origin} />
      <RankedBars title={translate("analyticsOpensBrowser")} breakdown={technical.opens.browser} dimension="browser" />
      <RankedTable title={translate("analyticsConversationsBrowser")} breakdown={technical.conversations.browser} />
      <RankedTable title={translate("analyticsConversationsOsVersion")} breakdown={technical.conversations.os_version} />
      <RankedTable title={translate("analyticsConversationsOrigin")} breakdown={technical.conversations.origin} />
    </Section>
  );
};

export default TechnicalSection;
